import {
  Avatar,
  Box,
  ChevronRightIcon,
  Typography
} from "@dealer-center/core-components";
import type { UserSummary } from "@dealer-center/ddc-pages-configuration-interface";
import {
  ON_SURFACE_INVERSE,
  ON_SURFACE_MUTED,
  SURFACE_INTERACTIVE_REST,
  desktopOnly,
  mobileOnly
} from "../../../components/UserInfo";

/**
 * The prototype truncates each of the row's three values rather than letting it overflow the rail —
 * `.user-name` / `.user-meta` are `white-space: nowrap; overflow: hidden; text-overflow: ellipsis`.
 *
 * It only works because the text column beside the avatar is a **flex column with `minWidth: 0`**:
 * `text-overflow` needs a block box, and a flex item defaults to `min-width: auto`, which refuses to
 * shrink below its content. The prototype's own CSS comment records the same trap — long emails ran
 * together on one line until `.user-info` became a flex column.
 */
const TRUNCATE = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const;

export interface UserRowProps {
  user: UserSummary;
  isSelected: boolean;
  /**
   * Selects this row.
   *
   * **The row, not its `ListItem` wrapper, is the interactive element** — see the component comment.
   * `ListItem`'s own `onClick` is left wired as well so a mouse click on the padding still works;
   * both call the same handler, and selecting an already-selected row is a no-op.
   */
  onSelect: () => void;
}

/**
 * `firstName` + `lastName`, trimmed, falling back to `userId`.
 *
 * Duplicated from `useUserManagerSubject` deliberately rather than shared: the row renders before
 * anything is selected, so it cannot read the resolved subject. Both apply the same rule for the
 * same reason — CC9 returns `""` (not `null`) for unset strings, so a user with neither name is a
 * real case and a blank row is the failure.
 */
export const resolveRowDisplayName = (user: UserSummary): string => {
  const name = [user.firstName, user.lastName]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();

  return name || user.userId;
};

/**
 * One row of the user list — an **avatar** beside **three values, each on its own line** (FR-010,
 * revised 2026-09-09): display name, username, email. All three are carried by the list payload, so a
 * row costs no additional request.
 *
 * **The avatar is the prototype's `.avatar` inside `.user-row`**, and it was missing from the first
 * build. It carries the display name's first letter, is `aria-hidden` because it repeats the name
 * beside it, and takes Interstate's `sm` step (32px) — the prototype draws 36px and the scale has no
 * step for it, so this follows `IdentityHeader`'s precedent of taking the nearest step rather than an
 * off-scale `sx` size. `sm` over `md` (40px) because the row is only three short lines tall and a
 * 40px circle crowds them.
 *
 * **Three things are deliberately absent, and each is something a later reader will be tempted to
 * add back:**
 *
 * 1. **No last-sign-in line.** It was removed from the row on 2026-09-09 and **FR-010a is retired**
 *    (**D-19**). The payload carries the timestamp under **both** `lastSignIn` and CC9's own
 *    `lastLogin` — the BFF forwards CC9's row whole as of 2026-09-17 — and **neither has a
 *    consumer**. This component formats no dates and **must not** import `formatLoginDate`. Do not
 *    derive a never-signed-in state, an activity badge, or a default sort order from either spelling;
 *    any of those re-creates FR-010a without re-ratifying it. The row also carries CC9's `picture`
 *    for the same reason, and the avatar below is still the display name's **initial**, never that
 *    image — the prototype draws no photo.
 * 2. **No administrator badge.** The prototype draws one; FR-010 drops it and **D-04** parks it. The
 *    list payload carries no such flag, and the only authoritative source is the per-user
 *    feature-access response — one ~186-row request per row against the slowest endpoints in the
 *    system. Three workarounds are explicitly FORBIDDEN: deriving admin status from the `-admin`
 *    `userId` suffix (removed from the BFF on 2026-08-18 as non-authoritative), fetching feature
 *    access per row, and inferring it from anything else on the row. Administrator status **is**
 *    still shown — on the Feature Access tab for the selected user (FR-045), which costs nothing.
 * 3. **No bold-red row highlighting.** The legacy tool had it, but it flagged **group managers** — a
 *    different concept entirely — so reproducing it here would assert something the data does not say.
 *
 * **Selection is conveyed visually by the enclosing `List`** (`selectable` + `selected`, its own
 * mechanism) **and programmatically by `aria-pressed` on the row itself** (FR-016).
 *
 * ⚠️ **There used to be a visually-hidden `SELECTED` marker here as well. Do not add it back.** It
 * predated the `role="button"` + `aria-pressed` fix below, and was the only accessible conveyance
 * available while the row's semantics came entirely from Interstate's `ListItem` — which exposes no
 * aria props at all, not `aria-selected`, not `aria-current`. Once the row became a real toggle
 * button the marker was redundant, and redundant is not harmless: a screen reader read both, so
 * every selected row announced "Selected, Jane Doe, button, **pressed**". Removed in review
 * 2026-09-22. `aria-pressed` is the native mechanism and the one to keep; if the marker ever looks
 * necessary again, the missing piece is a `listbox`/`option` pattern (see below), not a second
 * announcement.
 *
 * ## ⚠️ The row carries its own interactive semantics, because `ListItem` carries none
 *
 * **Added 2026-09-14 to fix a hard WCAG 2.1.1 (Keyboard, Level A) failure.** `ListItem` renders a
 * plain `<div>` — verified against the rendered DOM: `<div class="ids-list-item MuiBox-root" …>`,
 * with **no `role`, no `tabIndex`**, and `onClick` attached to it. So before this:
 *
 * - **no row was focusable**, which made the page unusable without a mouse — a keyboard user could
 *   not select a user *at all*, and selecting a user is the only way to reach anything else here;
 * - **no row announced itself as interactive**, so a screen-reader user had no reason to try.
 *
 * FR-075 requires both panels to be "reachable and operable by keyboard" and SC-010 allows zero
 * WCAG violations, so this was not a polish item. Caught by `UserManager.a11y.spec.tsx`'s
 * "selects a row by keyboard" case; nothing else could have caught it, because every other test
 * selects a row by **click**.
 *
 * `role="button"` + `aria-pressed` + `tabIndex={0}` + Enter/Space is the minimal conformant fix and
 * claims no contract that is not implemented. **A `listbox`/`option` pattern would be richer** — it
 * would convey selection as `aria-selected`, which is a truer fit than a toggle button for "one of
 * these rows is the current one" — but ARIA APG expects a listbox to support arrow-key navigation
 * with a roving tabindex, which is a larger change than a defect fix should smuggle in. Recorded as
 * the better end state rather than done here.
 *
 * `preventDefault` on Space is required: on a focusable non-button element the browser scrolls the
 * page instead, so the row would select *and* the list would jump.
 *
 * ### `aria-pressed` stays at every width, including the mobile drill-down
 *
 * TA4236901's plan called for dropping it below the breakpoint, on the reasoning that a row which
 * *navigates* is not a toggle. **That reasoning does not hold here, so it was not done.** Selecting
 * a user on mobile does two things — it sets the selection *and* shows the detail view — and the
 * selection genuinely persists: FR-006 and FR-017 require it to survive a filter, a sort, a page
 * change, and (per the drill-down) a trip back to the list. So `aria-pressed` keeps stating
 * something true and useful, namely which user you last opened. Dropping it would lose that.
 *
 * It would also have to be dropped in **JavaScript** — an attribute cannot be set by a media query
 * — which would mean `useWindowDimensions` and its first-paint correction for an a11y attribute.
 */
export const UserRow = ({ user, isSelected, onSelect }: UserRowProps) => {
  const displayName = resolveRowDisplayName(user);

  return (
    <Box
      aria-pressed={isSelected}
      data-testid={"user-row"}
      role={"button"}
      sx={{ alignItems: "flex-start", columnGap: "12px", display: "flex" }}
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {/*
       * ⚠️ **`aria-hidden` goes on the WRAPPER, because `Avatar` drops it.** Interstate's `Avatar`
       * destructures its known props and forwards only `data-testid` and `sizes` out of `rest`, so
       * an `aria-hidden` passed to it never reaches the DOM — verified against the component, and
       * caught by `UserRow.spec.tsx` when the assertion failed against a rendered avatar that was
       * fully exposed. The prototype marks it `aria-hidden` for a reason: the initial repeats a
       * letter of the name printed immediately beside it, so a screen reader reading the list would
       * announce a bare "J", "C", "B" before every row.
       *
       * (`IdentityHeader` had the same defect and it is fixed there now, the same way.)
       *
       * `&.MuiAvatar-root` is load-bearing, for the reason `IdentityHeader` records at length:
       * Interstate styles its own avatar with a self-referencing two-class rule, which silently
       * beats a plain `sx` object, and the avatar renders default grey with the colours dropped on
       * the floor. Matching that specificity is what makes the approved blue apply.
       *
       * **Desktop only (TA4236901).** The avatar renders a single letter of the name printed
       * immediately beside it — the `picture` field the payload carries is rendered by nothing — so
       * it holds no information, and being `aria-hidden` it holds none for assistive tech either.
       * At 390px its ~40px (30px + the 12px gap) is better spent on the values that truncate.
       */}
      <Box aria-hidden={"true"} sx={{ flexShrink: 0, ...desktopOnly("block") }}>
        <Avatar
          data-testid={"user-row-avatar"}
          sizes={"sm"}
          sx={{
            "&.MuiAvatar-root": {
              backgroundColor: SURFACE_INTERACTIVE_REST,
              color: ON_SURFACE_INVERSE
            }
          }}
        >
          {displayName.trim().charAt(0).toUpperCase()}
        </Avatar>
      </Box>

      {/*
       * `minWidth: 0` is what lets the three values truncate — see `TRUNCATE`.
       *
       * `flex: 1` is new, and is what pushes the mobile chevron below to the row's far edge. At
       * desktop there is no chevron, so it changes nothing there.
       */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minWidth: 0,
          rowGap: "2px"
        }}
      >
        <Typography
          data-testid={"user-row-name"}
          sx={TRUNCATE}
          tag={"p"}
          variant={"body-md"}
        >
          {displayName}
        </Typography>

        <Typography
          color={ON_SURFACE_MUTED}
          data-testid={"user-row-username"}
          sx={TRUNCATE}
          tag={"p"}
          variant={"body-sm"}
        >
          {user.userId}
        </Typography>

        {/*
         * **Desktop only (TA4236901).** The mobile row shows two values, not three.
         *
         * Email is the one dropped rather than `userId`, for two reasons: `userId` is what the list
         * is sorted and filtered by (so it is the value that explains a search result), and it is
         * far shorter, so it survives a 390px row where an address like
         * `test_bridge315@ddcdemo.dealer.com` truncates to nothing useful. The mockup is ambiguous
         * on this point — its first row shows an email and its other two show usernames, which is
         * inconsistent mock data rather than a rule.
         */}
        <Typography
          color={ON_SURFACE_MUTED}
          data-testid={"user-row-email"}
          sx={{ ...TRUNCATE, ...desktopOnly("block") }}
          tag={"p"}
          variant={"body-sm"}
        >
          {user.email}
        </Typography>
      </Box>

      {/*
       * The affordance for the row's *mobile* behaviour: at this width selecting a user also
       * navigates to the detail view, and a chevron is how a list row says so. Decorative —
       * `aria-hidden` — because the row already announces itself as a button with a name.
       */}
      <Box
        aria-hidden={"true"}
        sx={{
          alignItems: "center",
          alignSelf: "center",
          color: ON_SURFACE_MUTED,
          flexShrink: 0,
          ...mobileOnly("flex")
        }}
      >
        <ChevronRightIcon data-testid={"user-row-chevron"} title={""} />
      </Box>
    </Box>
  );
};
